// import coconut from "coconutjs";

import WebSocket from 'isomorphic-ws';

import { getUrl } from './sourceVideos';
import {
  getCocountFormatEntry,
  getLocations,
  processingDefinitions,
} from './processingDefinitions';
import { bulkProcessingJobs, VPSBulkJobKey } from './bulkProcessingJobs';
import { VPSEnvironmentKey, environments } from './environments';

const TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const CHECK_INTERVAL_MS = 5000; // 5 seconds

/**
 * Generates a unique job ID for tracking video processing tasks.
 * Uses a random string prefixed with underscore to ensure uniqueness.
 */
const generateJobId = () => {
  // Math.random should be unique because of its seeding algorithm.
  // Convert it to base 36 (numbers + letters), and grab the first 9 characters
  // after the decimal.
  return '_' + Math.random().toString(36).substr(2, 9);
};

export interface VPSProcessingResult {
  id: string;
  status: 'completed' | 'failed';
  message?: string;
}

/**
 * Checks if the coconut job information file exists, indicating processing is complete.
 * WARNING: Will not work at the moment for re-processing jobs; will return a false positive.
 */
async function checkCompletionJsonExists(
  sourceVideoKey: string,
  environmentKey: VPSEnvironmentKey,
): Promise<boolean> {
  // Get the CloudFront base URL from the environment
  const environment = environments[environmentKey];
  const cloudfrontBase = environment.outputHost;
  const completionUrl = `${cloudfrontBase}/videos/coconutJobs/latestBySourceKey/${sourceVideoKey}.json`;

  try {
    const res = await fetch(completionUrl, { method: 'HEAD' });
    return res.ok;
  } catch (error) {
    console.error(`Error checking completion JSON for ${sourceVideoKey}:`, error);
    return false;
  }
}

/**
 * Initiates video processing by sending instructions via WebSocket and monitoring for the completion JSON file.
 * The process is considered complete when the completion JSON file exists at the CloudFront URL.
 */
export default async function beginProcessing(
  sourceVideoKey: string,
  bulkProcessingJobKey: VPSBulkJobKey,
  environmentKey: VPSEnvironmentKey,
  websocketEndpoint: string,
): Promise<VPSProcessingResult> {
  const sourceUrl = getUrl(sourceVideoKey, environmentKey);
  const processingDefinitionKeys = bulkProcessingJobs[bulkProcessingJobKey].processingDefinitions;

  const jobInstructions = {
    source: sourceUrl,
    outputs: Object.fromEntries(
      processingDefinitionKeys.map((processingDefinitionKey) =>
        getCocountFormatEntry(sourceVideoKey, processingDefinitionKey, environmentKey, {
          accessKeyId: '{{COCONUT_AWS_ACCESS_KEY_ID}}',
          secretAccessKey: '{{COCONUT_AWS_SECRET_ACCESS_KEY}}',
        }),
      ),
    ),
  };

  const jobId = generateJobId();

  // Send the processing instructions via WebSocket
  const connection = new WebSocket(websocketEndpoint);
  await new Promise((resolve, reject) => {
    connection.onopen = resolve;
    connection.onerror = reject;
  });

  const message = {
    action: 'sendmessage',
    data: {
      id: jobId,
      source: jobInstructions.source,
      outputs: jobInstructions.outputs,
    },
  };
  connection.send(JSON.stringify(message));
  connection.close();

  // Now start checking for the completion JSON file
  return new Promise((resolve) => {
    let isResolved = false;

    const checkInterval = setInterval(async () => {
      try {
        const completionExists = await checkCompletionJsonExists(sourceVideoKey, environmentKey);

        if (completionExists && !isResolved) {
          isResolved = true;
          clearInterval(checkInterval);
          resolve({
            id: jobId,
            status: 'completed',
          });
        }
      } catch (error) {
        console.error('Error in completion JSON check interval:', error);
        if (!isResolved) {
          isResolved = true;
          clearInterval(checkInterval);
          resolve({
            id: jobId,
            status: 'failed',
            message: `Error checking completion JSON: ${
              error instanceof Error ? error.message : 'Unknown error'
            }`,
          });
        }
      }
    }, CHECK_INTERVAL_MS);

    // Set a timeout to prevent infinite checking
    setTimeout(() => {
      if (!isResolved) {
        isResolved = true;
        clearInterval(checkInterval);
        resolve({
          id: jobId,
          status: 'failed',
          message: 'Timeout waiting for completion JSON to be created',
        });
      }
    }, TIMEOUT_MS);
  });
}
